/**
 * Manual retry (D-51). The preview shows what a retry would do and which credential each channel would use
 * now, flagging one that has not changed since it failed, so support fixes the cause before sending again.
 * The retry itself runs in batches, with progress, until the selection is done.
 */
import {
  type Channel,
  RETRY_ACTIONS,
  type RetryCredentialCheck,
  type RetryFilter,
  type RetryInput,
  type RetryPreview,
} from "@aiyo/shared";
import { Button, ProgressBar, toast } from "@heroui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "../lib/format";
import { invalidateAfterRetry, runRetryBatch, useRetryPreview, useTestCredential } from "../lib/queries";
import { FormDialog } from "./dialogs";
import { CheckboxInput, SelectInput } from "./fields";
import { IssueChip } from "./issues";
import { Callout, ChannelChip, Code, InlineError, LoadingBlock } from "./ui";

export type RetrySelection = { delivery_ids: string[] } | { filter: RetryFilter };

const KEEP = "__keep__";
const BATCH = 500;

type Totals = { resent: number; added: number; unchanged: number };

export function RetryDialog({
  isOpen,
  onOpenChange,
  selection,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selection: RetrySelection;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [includeUnknown, setIncludeUnknown] = useState(false);
  const [overrides, setOverrides] = useState<Partial<Record<Channel, string>>>({});
  const [progress, setProgress] = useState<Totals | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Every opening starts from the plain preview.
  useEffect(() => {
    if (!isOpen) return;
    setIncludeUnknown(false);
    setOverrides({});
    setProgress(null);
    setError(null);
  }, [isOpen]);

  const body: RetryInput = {
    ...selection,
    include_unknown_outcome: includeUnknown,
    ...(Object.keys(overrides).length ? { credentials: overrides } : {}),
  };
  const preview = useRetryPreview(body, isOpen);
  const p = preview.data;

  const run = async () => {
    setRunning(true);
    setError(null);
    const totals: Totals = { resent: 0, added: 0, unchanged: 0 };
    setProgress({ ...totals });
    let after: string | undefined;
    try {
      for (;;) {
        const r = await runRetryBatch({ ...body, after, limit: BATCH });
        totals.resent += r.resent;
        totals.added += r.added;
        totals.unchanged += r.unchanged;
        setProgress({ ...totals });
        if (!r.next_cursor) break;
        after = r.next_cursor;
      }
      const sent = totals.resent + totals.added;
      if (totals.unchanged) {
        toast.success(t("retry.done", { count: sent }), {
          description: t("retry.doneUnchanged", { count: totals.unchanged }),
        });
      } else {
        toast.success(t("retry.done", { count: sent }));
      }
      onOpenChange(false);
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
      invalidateAfterRetry(qc);
    }
  };

  return (
    <FormDialog
      isOpen={isOpen}
      onOpenChange={(open) => !running && onOpenChange(open)}
      size="lg"
      title={t("retry.title")}
      description={t("retry.description")}
      submitLabel={p ? t("retry.submit", { count: p.retryable }) : t("retry.submitPlain")}
      isPending={running}
      isSubmitDisabled={!p || p.retryable === 0 || preview.isFetching}
      error={error}
      onSubmit={run}
    >
      {preview.isPending ? (
        <LoadingBlock />
      ) : preview.isError ? (
        <InlineError error={preview.error} />
      ) : p ? (
        <div
          className={`flex flex-col gap-6 transition-opacity ${preview.isPlaceholderData ? "opacity-60" : ""}`}
        >
          <Summary p={p} includeUnknown={includeUnknown} onIncludeUnknown={setIncludeUnknown} />
          {p.credentials.length ? (
            <Credentials
              checks={p.credentials}
              overrides={overrides}
              onOverride={(channel, alias) =>
                setOverrides((o) => {
                  const next = { ...o };
                  if (alias) next[channel] = alias;
                  else delete next[channel];
                  return next;
                })
              }
            />
          ) : null}
          {progress ? <Progress done={progress} total={p.retryable} /> : null}
        </div>
      ) : null}
    </FormDialog>
  );
}

function Summary({
  p,
  includeUnknown,
  onIncludeUnknown,
}: {
  p: RetryPreview;
  includeUnknown: boolean;
  onIncludeUnknown: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  if (p.total === 0) return <Callout tone="info">{t("retry.nothing")}</Callout>;
  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm">{t("retry.summary", { count: p.retryable, total: fmt.number(p.total) })}</p>
      {RETRY_ACTIONS.map((action) => {
        const groups = p.groups.filter((g) => g.action === action);
        if (!groups.length) return null;
        const count = groups.reduce((n, g) => n + g.count, 0);
        return (
          <section key={action} className="flex flex-col gap-2.5">
            <div className="flex flex-col gap-0.5">
              <h3 className="text-sm font-semibold">
                {t(`retry.actions.${action}.title`)}
                <span className="font-normal text-muted"> · {fmt.number(count)}</span>
              </h3>
              <p className="text-xs leading-relaxed text-muted">{t(`retry.actions.${action}.hint`)}</p>
            </div>
            <ul className="flex flex-col gap-2">
              {groups.map((g) => (
                <li key={`${g.channel}-${g.issue}`} className="flex flex-wrap items-center gap-2">
                  <IssueChip issue={g.issue} />
                  <ChannelChip channel={g.channel} />
                  <span className="ml-auto text-sm tabular-nums">{fmt.number(g.count)}</span>
                </li>
              ))}
            </ul>
            {action === "confirm" ? (
              <div className="flex flex-col gap-2">
                <CheckboxInput
                  label={t("retry.includeUnknown")}
                  isSelected={includeUnknown}
                  onChange={onIncludeUnknown}
                />
                {includeUnknown ? <Callout tone="warning">{t("retry.unknownWarning")}</Callout> : null}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function Credentials({
  checks,
  overrides,
  onOverride,
}: {
  checks: RetryCredentialCheck[];
  overrides: Partial<Record<Channel, string>>;
  onOverride: (channel: Channel, alias: string | null) => void;
}) {
  const { t } = useTranslation();
  const apps = new Set(checks.map((c) => c.app.id));
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold">{t("retry.credentials.title")}</h3>
        <p className="text-xs leading-relaxed text-muted">
          {apps.size > 1 ? t("retry.credentials.hintManyApps") : t("retry.credentials.hint")}
        </p>
      </div>
      {checks.map((c) => (
        <CredentialCheck
          key={`${c.app.id}-${c.channel}`}
          check={c}
          showApp={apps.size > 1}
          override={apps.size === 1 ? (overrides[c.channel] ?? null) : undefined}
          onOverride={(alias) => onOverride(c.channel, alias)}
        />
      ))}
    </section>
  );
}

function CredentialCheck({
  check: c,
  showApp,
  override,
  onOverride,
}: {
  check: RetryCredentialCheck;
  showApp: boolean;
  /** Undefined when the selection spans several apps, where a credential cannot be chosen. */
  override: string | null | undefined;
  onOverride: (alias: string | null) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const test = useTestCredential();
  const result = test.data;
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-surface-secondary p-4">
      <div className="flex flex-wrap items-center gap-2">
        <ChannelChip channel={c.channel} />
        {showApp ? <span className="text-sm font-medium">{c.app.name}</span> : null}
        <span className="ml-auto text-xs text-muted">
          {t("retry.credentials.deliveries", { count: c.deliveries })}
        </span>
      </div>

      {c.credential ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="text-muted">{t("retry.credentials.willUse")}</span>
          <Code>{c.credential.alias}</Code>
          <span className="font-medium">{c.credential.name}</span>
          <span className="text-xs text-muted">
            {c.credential.provider} ·{" "}
            {t("retry.credentials.edited", { when: fmt.relative(c.credential.updated_at) })}
          </span>
          <Button
            size="sm"
            variant="tertiary"
            className="ml-auto"
            isPending={test.isPending}
            onPress={() => test.mutate(c.credential!.id)}
          >
            {t("credentials.test")}
          </Button>
        </div>
      ) : null}
      {result ? (
        result.ok ? (
          <Callout tone="success">{t("credentials.testOk")}</Callout>
        ) : (
          <Callout tone="warning">
            {t("credentials.testFailed")}
            {result.error ? `: ${result.error}` : ""}
          </Callout>
        )
      ) : null}

      {c.error ? (
        <Callout tone="warning">
          {t("retry.credentials.error")} <span className="text-xs">({c.error.message})</span>
        </Callout>
      ) : c.unchanged ? (
        <Callout tone="warning">{t("retry.credentials.unchanged")}</Callout>
      ) : null}

      {override !== undefined && c.options.length > 0 ? (
        <SelectInput
          label={t("retry.credentials.use")}
          value={override ?? KEEP}
          onChange={(v) => onOverride(!v || v === KEEP ? null : v)}
          options={[
            { id: KEEP, label: t("retry.credentials.keep") },
            ...c.options.map((o) => ({
              id: o.alias,
              label: `${o.alias} · ${o.credential_name}`,
              hint: o.usable ? o.provider : t("retry.credentials.unusable"),
            })),
          ]}
        />
      ) : null}
    </div>
  );
}

function Progress({ done, total }: { done: Totals; total: number }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const handled = done.resent + done.unchanged;
  const pct = total ? Math.min(100, Math.round((handled / total) * 100)) : 100;
  return (
    <div className="flex flex-col gap-2">
      <ProgressBar aria-label={t("retry.progress")} value={pct} size="sm">
        <ProgressBar.Track>
          <ProgressBar.Fill />
        </ProgressBar.Track>
      </ProgressBar>
      <p className="text-xs text-muted">
        {t("retry.progressText", { done: fmt.number(handled), total: fmt.number(total) })}
      </p>
    </div>
  );
}
